'use client';

import React, { useState, useCallback, KeyboardEvent } from 'react';
import { Search } from 'lucide-react';
import { cn } from '../utils/cn';
import type { SearchLineProps } from './types';

/**
 * Компонент строки поиска с поддержкой многоязычности
 */
export function SearchLine({
  translations = {},
  buildPath,
  value: controlledValue,
  onChange,
  onSearch,
  className = '',
  InputComponent,
  suggestions,
  popularQueries,
  onSuggestionsFetch,
}: SearchLineProps) {
  const [internalValue, setInternalValue] = useState('');
  const isControlled = controlledValue !== undefined;
  const value = isControlled ? controlledValue : internalValue;

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      if (!isControlled) {
        setInternalValue(newValue);
      }
      onChange?.(newValue);
    },
    [isControlled, onChange]
  );

  const handleSearch = useCallback(() => {
    const query = value.trim();
    if (!query) return;

    if (onSearch) {
      onSearch(query);
      return;
    }

    // Если onSearch не передан, формируем URL самостоятельно
    const searchPath = `/search?query=${encodeURIComponent(query)}`;
    const finalPath = buildPath ? buildPath(searchPath) : searchPath;
    window.location.href = finalPath;
  }, [value, onSearch, buildPath]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSearch();
      }
    },
    [handleSearch]
  );

  const Input = InputComponent || 'input';
  const placeholder = translations.placeholder || 'Поиск...';
  const searchIconSize = 16;

  return (
    <div className={cn('relative', className || 'w-auto')}>
      <Input
        type="search"
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={cn(
          'pr-12 h-10 bg-white border-gray-200 rounded-md border',
          'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500',
          InputComponent ? '' : 'w-full'
        )}
      />
      <button
        type="button"
        onClick={handleSearch}
        className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center justify-center p-1.5 rounded-md hover:bg-gray-100 transition-colors z-10"
        aria-label={translations.search || 'Найти'}
      >
        <Search className="h-4 w-4 text-gray-600" size={searchIconSize} />
      </button>
      {/* TODO: Добавить отображение подсказок и популярных запросов */}
    </div>
  );
}
